import { Mic, Square, X } from 'lucide-react';
import { NamespaceBoundary } from '@/lib/i18n/NamespaceBoundary';
import { useTranslation } from '@/lib/i18n/react';
import { formatVoiceNoteDuration } from '../hooks/useVoiceNoteRecorder';

interface VoiceRecordingBarProps {
  duration: number;
  onCancel: () => void;
  onStop: () => void;
  stopLabel?: string;
  isStarting?: boolean;
}

export function VoiceRecordingBar(props: VoiceRecordingBarProps) {
  return <NamespaceBoundary namespace="teamchat"><RecordingControls {...props} /></NamespaceBoundary>;
}

function RecordingControls({ duration, onCancel, onStop, stopLabel, isStarting = false }: VoiceRecordingBarProps) {
  const { t } = useTranslation('teamchat');
  const actionLabel = stopLabel ?? t('voice.stop');
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2 rounded-sm border border-red-200 bg-red-50 px-2.5 py-2 text-red-700">
      <span className="relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
        {!isStarting && <span className="absolute inset-0 animate-ping rounded-full bg-red-300/50" />}
        <Mic size={15} className="relative" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1 basis-20">
        <span role="status" className="block break-words text-xs font-bold">{t(isStarting ? 'voice.requesting' : 'voice.recording')}</span>
        <span dir="ltr" className="font-mono text-[11px] tabular-nums">
          {formatVoiceNoteDuration(duration)}
        </span>
      </span>
      <button
        type="button"
        onClick={onCancel}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm text-red-500 hover:bg-red-100 hover:text-red-700"
        title={t('voice.cancel')}
        aria-label={t('voice.cancel')}
      >
        <X size={15} aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onStop}
        disabled={isStarting}
        className="inline-flex min-h-11 min-w-11 max-w-full items-center gap-1.5 rounded-sm bg-red-600 px-2.5 py-2 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50"
        title={actionLabel}
      >
        <Square size={11} fill="currentColor" className="shrink-0" aria-hidden="true" />
        <span className="break-words">{actionLabel}</span>
      </button>
    </div>
  );
}
